/**
 * Formate un nombre en format monétaire (avec séparateurs de milliers)
 * @param value - La valeur à formater (string, number ou undefined)
 * @returns La chaîne formatée avec des espaces comme séparateurs de milliers
 */
export const formatPrice = (value: string | number | undefined): string => {
  if (!value) return '';

  // Nettoyer la valeur pour ne garder que les chiffres et le point décimal
  const numericValue = Math.floor(Number(value.toString().replace(/[^\d.-]/g, ''))).toString();

  // Ajouter un espace tous les trois chiffres pour les milliers
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

/**
 * Formate une surface en supprimant les décimales
 * @param value - La valeur à formater (string, number ou undefined)
 * @returns La chaîne formatée sans décimales
 */
export const formatArea = (value: string | number | undefined): string => {
  if (!value) return '';

  // Nettoyer et convertir en nombre entier
  const numericValue = Math.floor(Number(value.toString().replace(/[^\d.-]/g, '')));

  return numericValue.toString();
};

/**
 * Formate un prix pour l'affichage dans les champs de formulaire (sans séparateurs)
 * @param value - La valeur à formater (string, number ou undefined)
 * @returns La chaîne formatée sans décimales ni séparateurs
 */
export const formatPriceForInput = (value: string | number | undefined): string => {
  if (!value) return '';

  // Nettoyer et convertir en nombre entier
  const numericValue = Math.floor(Number(value.toString().replace(/[^\d.-]/g, '')));

  return numericValue.toString();
};

/**
 * Formate une surface en autorisant les décimales (pour surface habitable et utile)
 * @param value - La valeur à formater (string, number ou undefined)
 * @returns La chaîne formatée avec décimales si présentes
 */
export const formatAreaWithDecimals = (value: string | number | undefined): string => {
  if (!value) return '';

  // Nettoyer la valeur pour ne garder que les chiffres et le point décimal
  const cleanedValue = value.toString().replace(/[^\d.-]/g, '');

  // Si la valeur est vide après nettoyage, retourner une chaîne vide
  if (!cleanedValue) return '';

  return cleanedValue;
};

/**
 * Parse une chaîne formatée en nombre (en ignorant les caractères non numériques)
 * @param value - La chaîne à parser
 * @returns Le nombre entier résultant
 */
export const parsePrice = (value: string): number => {
  return parseInt(value.replace(/\D/g, ''), 10) || 0;
};

/**
 * Formate une date au format français (jj/mm/aaaa)
 * @param dateString - La date au format ISO
 * @returns La date formatée au format français
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

export const formatters = {
  formatPrice,
  formatArea,
  formatPriceForInput,
  formatAreaWithDecimals,
  parsePrice,
  formatDate,
};
